import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from 'reactstrap';
import { Icon } from 'js/components';
import {
  faCheck,
  faCopy,
  faFileArrowUp,
  faSparkles,
} from '@fortawesome/pro-regular-svg-icons';

import BriefingHeatmap from './BriefingHeatmap';
import BriefingItemChart from './BriefingItemChart';
import { chartPaletteById } from './briefing-chart-lines';
import { eachMark, MARK_RE } from './briefing-marks';
import BriefingVersionSelect from './BriefingVersionSelect';
import RegenerateBriefingIcon from './RegenerateBriefingIcon';
import { copyBriefingContent } from './copyBriefingContent';
import styles from './OrgAnalyze.module.scss';

// A safety net, not the main control: validation already sends back a
// briefing that is one bullet per measure, and low importance hides most of
// the rest.
const PREVIEW_LIMIT = 8;

// Items stay in the order the model wrote them, so a bullet that sets up the
// next one stays ahead of it. Collapsed, the low-importance items are left out
// where they stand; expanded, they appear back in place.
export function previewItems(items) {
  const all = items ?? [];
  const aboveLow = all.filter((item) => item.importance !== 'low');
  const pool = aboveLow.length > 0 ? aboveLow : all;
  return pool.slice(0, PREVIEW_LIMIT);
}

function presentNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function roundPoints(value) {
  if (!presentNumber(value)) {
    return null;
  }
  const abs = Math.abs(value);
  if (abs > 0 && abs < 2) {
    return Number.isInteger(abs)
      ? abs
      : Number(abs.toFixed(1).replace(/\.0$/, ''));
  }
  return Math.round(abs);
}

// Inside a span mark the model writes its own numbers, so the rounding the
// value marks used to guarantee is applied to the text instead. Mirrors
// OrgBriefingDocument.round_units/1 -- both renderers must agree.
const RANGE_RE = /(\d+(?:\.\d+)?)\s*[–—-]\s*(\d+(?:\.\d+)?)(\s*)(points?|%)/g;
const VALUE_RE = /(\d+(?:\.\d+)?)(\s*)(points?|%)/g;

function roundUnits(text) {
  return String(text)
    .replace(RANGE_RE, (full, low, high, space, unit) => {
      const lo = roundPoints(Number(low));
      const hi = roundPoints(Number(high));
      if (lo == null || hi == null) {
        return full;
      }
      return lo === hi ? `${lo}${space}${unit}` : `${lo}–${hi}${space}${unit}`;
    })
    .replace(VALUE_RE, (full, value, space, unit) => {
      const rounded = roundPoints(Number(value));
      return rounded == null ? full : `${rounded}${space}${unit}`;
    });
}

// One mark -> one styled node. Shared by AnnotatedText (plain mark-only text,
// as every briefing section item is) and by the chat renderer below (marks
// mixed freely with real markdown, since a chat reply is free-form).
function renderMark(type, value, key) {
  switch (type) {
    case 'team':
      return (
        <span key={key} className={styles.team}>
          {value}
        </span>
      );
    // A question set or category reads as a name the same way a measure does.
    case 'measure':
    case 'set':
      return (
        <span key={key} className={styles.measure}>
          {value}
        </span>
      );
    // Span marks. The model writes the phrase and its units and decides how
    // much of the sentence is the movement, so a clause like "jumping 42
    // points" and the level it reached can carry the same color.
    case 'up':
    case 'down':
      return (
        <span
          key={key}
          className={`${styles.move} ${
            type === 'down' ? styles.deltaDecline : styles.deltaImprove
          }`}
        >
          {roundUnits(value)}
        </span>
      );
    case 'chart':
      return null;
    default:
      return value;
  }
}

// Splits one string on [[mark:value]] occurrences into an array of plain
// strings and rendered mark nodes, in order.
function expandMarks(text, keyPrefix = 'mark') {
  if (typeof text !== 'string' || text.indexOf('[[') === -1) {
    return [text];
  }

  const parts = [];
  let lastIndex = 0;
  let index = 0;
  eachMark(text, (match) => {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(renderMark(match[1], match[2], `${keyPrefix}-${index}`));
    index += 1;
    lastIndex = match.index + match[0].length;
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// Prompts are things the reader might send as a question. The model still
// writes [[measure:Learning]] so it stays consistent with briefing prose,
// but a suggestion should read "Learning", not the mark syntax and not a chip.
export function stripMarks(text) {
  if (typeof text !== 'string' || text.indexOf('[[') === -1) {
    return text;
  }

  return text
    .replace(new RegExp(MARK_RE.source, 'g'), (_full, type, value) =>
      type === 'chart' ? '' : value
    )
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Exported so chat replies can style team/measure/movement marks the same
// way a briefing section does -- the model is told to use the identical
// vocabulary in both, and a chat reply rendered through plain markdown just
// prints the bracket syntax as literal text. Section item bodies are plain
// mark-annotated text with no markdown of their own, so this only ever needs
// to handle marks.
export function AnnotatedText({ text }) {
  if (!text) {
    return null;
  }
  return expandMarks(text);
}

// Runs mark expansion over one node of a markdown tree's children -- a
// string is scanned for marks, an already-rendered element (a nested
// <strong>, a link, ...) is left exactly as react-markdown produced it. Used
// to layer marks on top of full markdown without needing a custom
// remark/rehype plugin: react-markdown still owns headings, emphasis, lists,
// and paragraphs; this only ever touches the leaf text inside them.
function withMarks(children, keyPrefix) {
  const nodes = Array.isArray(children) ? children : [children];
  return nodes.flatMap((child, index) =>
    typeof child === 'string'
      ? expandMarks(child, `${keyPrefix}-${index}`)
      : [child]
  );
}

// strong/em must render real emphasis -- **bold** is not automatically a
// measure name just because it is bold. A model wrapping a mark for
// emphasis, e.g. **[[team:Demo]]**, is common and must not make the *rest of
// the message* collapse into one bold span: without this, "**" left dangling
// on either side of an extracted mark could re-pair with some unrelated "**"
// much later in the same text. A real [[measure:...]] mark still gets its
// chip styling regardless of whether it happens to sit inside **bold**.
function MarkedStrong({ children }) {
  return <strong>{withMarks(children, 'strong')}</strong>;
}

function MarkedEm({ children }) {
  return <em>{withMarks(children, 'em')}</em>;
}

function MarkedParagraph({ children }) {
  return <p>{withMarks(children, 'p')}</p>;
}

function MarkedListItem({ children }) {
  return <li>{withMarks(children, 'li')}</li>;
}

const MARKED_INLINE_COMPONENTS = {
  strong: MarkedStrong,
  em: MarkedEm,
  p: MarkedParagraph,
  li: MarkedListItem,
};

// A deliberately unshouty scale for chat headings, graduated by level rather
// than collapsed to one size -- react-markdown gives each h1..h6 its own slot
// in `components`, which is its equivalent of the "plugin" the model's output
// runs through; mapping all six to the same renderer with no level attached
// threw that information away; this keeps it, via one small wrapper per
// level (styles.chatHeading + a per-level modifier class, composed in scss).
// Briefing sections get the bold uppercase .sectionTitle treatment because
// there are only ever three of them, acting as real page structure. A chat
// reply can throw out a "#### 2. Chronic codebase debt" as an aside
// mid-explanation -- rendered at browser-default h1..h6 sizes that reads as
// shouting, and rendered as .sectionTitle it reads as a fourth "official"
// section that does not exist. This scale sits between the two.
// chatHeadingLevelN composes the shared .chatHeading base in scss, so this
// only ever needs the level-specific class.
function chatHeadingClass(level) {
  return styles[`chatHeadingLevel${level}`];
}

// Headings used to pass children through unmarked. A reply that puts a
// measure chip in a `####` title then printed `[[measure:...]]` as text.
function markedChatHeading(level) {
  return function ChatHeading({ children }) {
    return (
      <div className={chatHeadingClass(level)}>
        {withMarks(children, `h${level}`)}
      </div>
    );
  };
}

const CHAT_MARKDOWN_COMPONENTS = {
  ...MARKED_INLINE_COMPONENTS,
  h1: markedChatHeading(1),
  h2: markedChatHeading(2),
  h3: markedChatHeading(3),
  h4: markedChatHeading(4),
  h5: markedChatHeading(5),
  h6: markedChatHeading(6),
};

// Chat replies are free-form: the model mixes real markdown (headings, bold,
// lists) with the same [[mark:...]] vocabulary the briefing uses. Rendering
// through plain AnnotatedText (marks only) left "####" and "**" as literal
// text; rendering through plain ReactMarkdown (markdown only) printed marks
// as literal "[[team:...]]". Headings go through the same withMarks pass as
// paragraphs -- otherwise `#### [[measure:...]]` shows the brackets.
export function ChatMarkdown({ text }) {
  if (!text) {
    return null;
  }
  return (
    <ReactMarkdown components={CHAT_MARKDOWN_COMPONENTS}>{text}</ReactMarkdown>
  );
}

function SectionHeading({ children }) {
  return <h3 className={styles.sectionTitle}>{children}</h3>;
}

const BRIEFING_MARKDOWN_COMPONENTS = {
  ...MARKED_INLINE_COMPONENTS,
  h1: SectionHeading,
  h2: SectionHeading,
  h3: SectionHeading,
};

function isOrgWideChart(item) {
  return item?.chart?.kind === 'measures';
}

// Past this many series a merged figure's legend stops being readable. One
// item's own chart is never cut down; this only stops items from piling
// their series into one figure.
const MAX_MERGED_LINES = 6;

function chartLineIds(items) {
  const ids = new Set();
  for (const item of items) {
    for (const line of item.chart?.lines ?? []) {
      const id = line.id || line.label;
      if (id) {
        ids.add(id);
      }
    }
  }
  return ids;
}

// Adjacent org-wide measure charts become one figure so their bullets can
// wrap a single float instead of each item clearing its own plot -- unless
// the merged figure would carry more series than it can show legibly, in
// which case the item keeps its own chart.
export function groupSectionItems(items) {
  const groups = [];
  for (const item of items) {
    const current = groups[groups.length - 1];
    if (
      isOrgWideChart(item) &&
      current?.type === 'orgWide' &&
      chartLineIds([...current.items, item]).size <= MAX_MERGED_LINES
    ) {
      current.items.push(item);
      continue;
    }
    groups.push({
      type: isOrgWideChart(item) ? 'orgWide' : 'single',
      items: [item],
    });
  }
  return groups;
}

function mergedOrgWideChart(items) {
  const lines = [];
  const seen = new Set();
  for (const item of items) {
    for (const line of item.chart?.lines ?? []) {
      const id = line.id || line.label;
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      lines.push(line);
    }
  }
  if (lines.length === 0) {
    return null;
  }
  if (items.length === 1) {
    return items[0].chart;
  }
  return {
    kind: 'measures',
    title: items[0].chart?.title || 'Org-Wide Selected Trends',
    lines,
  };
}

function ItemGroup({ group, paletteById, onFocusItem }) {
  const chart =
    group.type === 'orgWide'
      ? mergedOrgWideChart(group.items)
      : group.items[0].chart;
  const figure = chart ? (
    <BriefingItemChart chart={chart} paletteById={paletteById} />
  ) : null;

  return (
    <div
      className={
        figure ? `${styles.item} ${styles.itemWithChart}` : styles.item
      }
    >
      {figure}
      <ul className={styles.itemBullet}>
        {group.items.map((item, index) => (
          <li
            key={index}
            onMouseEnter={onFocusItem ? () => onFocusItem(item) : undefined}
            onMouseLeave={onFocusItem ? () => onFocusItem(null) : undefined}
          >
            <AnnotatedText text={item.body} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// One line per team, written with the briefing. They sit under Across the
// Teams, collapsed until the reader asks for them, and opened while copying
// so a pasted briefing includes every team.
function TeamReads({ reads, forceOpen }) {
  const [open, setOpen] = useState(false);
  if (!reads?.length) {
    return null;
  }
  const shown = open || forceOpen;

  return (
    <div className={styles.teamReads} data-test="briefing-team-reads">
      <Button
        color="link"
        className="px-0"
        aria-expanded={shown}
        onClick={() => setOpen((on) => !on)}
      >
        {shown ? 'Hide quick reads' : `Team quick reads (${reads.length})`}
      </Button>
      {shown ? (
        <ul className={styles.itemBullet}>
          {reads.map((read) => (
            <li key={read.team}>
              <strong>{read.team}</strong>: <AnnotatedText text={read.body} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Section({
  section,
  paletteById,
  reads = null,
  forceOpenReads = false,
  onFocusItem = null,
}) {
  const [expanded, setExpanded] = useState(false);
  const items = section.items ?? [];
  const visible = expanded ? items : previewItems(items);
  const groups = groupSectionItems(visible);
  const hiddenCount = Math.max(0, items.length - visible.length);

  if (items.length === 0) {
    return null;
  }

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{section.title}</h3>
      <div className={styles.itemList}>
        {groups.map((group, index) => (
          <ItemGroup
            key={`${section.id}-${index}`}
            group={group}
            paletteById={paletteById}
            onFocusItem={onFocusItem}
          />
        ))}
      </div>
      {hiddenCount > 0 ? (
        <Button
          color="link"
          className="px-0"
          onClick={() => setExpanded((on) => !on)}
        >
          {expanded ? 'View less' : `View more (${hiddenCount})`}
        </Button>
      ) : null}
      {reads ? <TeamReads reads={reads} forceOpen={forceOpenReads} /> : null}
    </section>
  );
}

// Across the Teams follows the team callouts, or the first section when a
// briefing has no callouts. The one-line reads sit in that section, collapsed
// until asked for. Pointing at a callout still lights the matching cells.
function HeatmapSection({
  section,
  paletteById,
  heatmap,
  document,
  reads,
  forceOpenReads,
  assessmentTemplate,
  isRubric,
}) {
  const [focusItem, setFocusItem] = useState(null);
  const readsByTeam = useMemo(
    () => new Map((reads ?? []).map((read) => [read.team, read.body])),
    [reads]
  );

  return (
    <>
      <Section
        section={section}
        paletteById={paletteById}
        onFocusItem={setFocusItem}
      />
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Across the Teams</h3>
        <BriefingHeatmap
          heatmap={heatmap}
          document={document}
          focusItem={focusItem}
          reads={readsByTeam}
          assessmentTemplate={assessmentTemplate}
          isRubric={isRubric}
        />
        <TeamReads reads={reads} forceOpen={forceOpenReads} />
      </section>
    </>
  );
}

function heatmapSectionId(sections) {
  if (sections.some((section) => section.id === 'team_callouts')) {
    return 'team_callouts';
  }
  return sections[0]?.id ?? null;
}

// Shared by the briefing generate stages and the chat tool-loop stages, so a
// stage key means the same thing wherever it is published. Anything without an
// entry falls back to the generic label rather than showing a raw key.
const GENERATE_STAGE_LABELS = {
  queued: 'Thinking',
  // Every briefing slot in the cluster is taken; the job asks again shortly.
  waiting_for_capacity: 'Waiting for an analysis slot',
  collecting_data: 'Looking through the scores',
  drafting: 'Writing the analysis',
  retrying: 'Revising the analysis',
  building_survey: 'Building survey questions',
  regenerating: 'Starting a new draft',
  testing_rubric: 'Testing that against the scores',
  // Tool rounds. Each stays up while the model reads what the tool returned,
  // so it describes the step, not just the tool call.
  running_numbers: 'Running the numbers',
  ranking_teams: 'Ranking the teams',
  checking_outliers: 'Checking for outliers',
  reading_teams: 'Pulling up team results',
  comparing_teams: 'Comparing teams',
  tracing_trends: 'Tracing the trend over time',
  finishing: 'Pulling it all together',
};

export function generateStageLabel(stage) {
  return GENERATE_STAGE_LABELS[stage] || 'Thinking';
}

export function ThinkingIndicator({ stage, decorative = false }) {
  return (
    <div
      className={styles.thinking}
      data-test="org-thinking"
      role={decorative ? undefined : 'status'}
      aria-hidden={decorative ? true : undefined}
    >
      <Icon icon={faSparkles} className={styles.thinkingIcon} />
      <span className={styles.thinkingLabel}>
        {generateStageLabel(stage)}
        <span className={styles.thinkingDots} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </span>
    </div>
  );
}

export default function OrgBriefing({
  briefing,
  // A briefing still being written (from generate_partial). Shown in place
  // of `briefing`'s document, undimmed; the finished briefing replaces it.
  streamingDocument = null,
  generating,
  thinkingStage,
  onRegenerate,
  onPublish,
  publishing = false,
  versions = [],
  selectedVersionId,
  onSelectVersion,
  currentUserId,
  assessmentTemplate = null,
  isRubric = false,
}) {
  const streaming = Boolean(streamingDocument);
  const document = streamingDocument ?? briefing?.document;
  const analysisSections = (document?.sections ?? []).filter(
    (section) => (section.items ?? []).length > 0
  );
  const paletteById = useMemo(() => chartPaletteById(document), [document]);
  const contentRef = useRef(null);
  const copiedTimer = useRef(null);
  const [copyState, setCopyState] = useState('idle');
  const hasCopyableContent = Boolean(
    !streaming &&
      (document?.intro || analysisSections.length > 0 || briefing?.body)
  );
  const showVersion = versions.length > 0 && Boolean(onSelectVersion);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const copyBriefing = useCallback(() => {
    if (!contentRef.current || copyState === 'copying') return;
    window.clearTimeout(copiedTimer.current);
    // The all-teams reads open while copying, so the copy is taken after the
    // render that shows them (the effect below).
    setCopyState('copying');
  }, [copyState]);

  useEffect(() => {
    const node = contentRef.current;
    if (copyState !== 'copying' || !node) return;
    copyBriefingContent(node).then(
      () => {
        setCopyState('copied');
        copiedTimer.current = window.setTimeout(
          () => setCopyState('idle'),
          2000
        );
      },
      () => {
        setCopyState('failed');
        copiedTimer.current = window.setTimeout(
          () => setCopyState('idle'),
          2000
        );
      }
    );
  }, [copyState]);

  const readsSectionId = heatmapSectionId(analysisSections);

  let copyLabel = 'Copy briefing';
  if (copyState === 'copied') copyLabel = 'Copied';
  if (copyState === 'failed') copyLabel = 'Could not copy the briefing';

  return (
    <div className={styles.briefing} data-test="org-briefing">
      {generating ? <ThinkingIndicator stage={thinkingStage} /> : null}

      {showVersion || hasCopyableContent ? (
        <div className={styles.briefingControls}>
          {hasCopyableContent ? (
            <button
              type="button"
              className={styles.actionIcon}
              data-test="org-briefing-copy"
              aria-label={copyLabel}
              title={copyLabel}
              disabled={copyState === 'copying'}
              onClick={copyBriefing}
            >
              <Icon icon={copyState === 'copied' ? faCheck : faCopy} />
            </button>
          ) : null}
          {showVersion ? (
            <BriefingVersionSelect
              versions={versions}
              value={selectedVersionId}
              onChange={onSelectVersion}
              currentUserId={currentUserId}
              disabled={generating}
            />
          ) : null}
        </div>
      ) : null}

      <div
        ref={contentRef}
        className={generating && !streaming ? styles.briefingStale : undefined}
      >
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Executive Summary</h3>
          {document?.intro ? (
            <ReactMarkdown components={MARKED_INLINE_COMPONENTS}>
              {document.intro}
            </ReactMarkdown>
          ) : null}
        </section>

        {analysisSections.length > 0 ? (
          analysisSections.map((section) => {
            const withTeams = !streaming && section.id === readsSectionId;
            if (withTeams && document?.heatmap) {
              return (
                <HeatmapSection
                  key={section.id || section.title}
                  section={section}
                  paletteById={paletteById}
                  heatmap={document.heatmap}
                  document={document}
                  reads={document?.team_reads}
                  forceOpenReads={copyState === 'copying'}
                  assessmentTemplate={assessmentTemplate}
                  isRubric={isRubric}
                />
              );
            }
            return (
              <Section
                key={section.id || section.title}
                section={section}
                paletteById={paletteById}
                reads={withTeams ? document?.team_reads : null}
                forceOpenReads={copyState === 'copying'}
              />
            );
          })
        ) : !streaming && briefing?.body ? (
          <ReactMarkdown components={BRIEFING_MARKDOWN_COMPONENTS}>
            {briefing.body}
          </ReactMarkdown>
        ) : null}
      </div>

      {onRegenerate || onPublish ? (
        <div className={styles.briefingActions}>
          <div className={styles.messageActionRow}>
            {onRegenerate ? (
              <button
                type="button"
                className={styles.actionChip}
                aria-label="Regenerate the briefing"
                title="Regenerate the briefing"
                disabled={generating}
                onClick={onRegenerate}
              >
                <RegenerateBriefingIcon spin={generating} />
                <span>Regenerate</span>
              </button>
            ) : (
              <button
                type="button"
                className={styles.actionChip}
                aria-label="Publish as the shared analysis"
                title="Publish as the shared analysis"
                disabled={generating || publishing}
                onClick={onPublish}
              >
                <Icon icon={faFileArrowUp} />
                <span>
                  {publishing
                    ? 'Publishing…'
                    : 'Publish as the shared analysis'}
                </span>
              </button>
            )}
          </div>
          {generating ? (
            <ThinkingIndicator stage={thinkingStage} decorative />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
